import axios from 'axios';
import { rootUrl } from '../../../config/config';

export const REPORT_LAYOUT_LABELS = {
    columns: 'Columns',
    rows: 'Rows',
    grid: 'Grid'
};

export const describeReportFormat = (report) => (report?.format === 'csv'
    ? 'CSV'
    : `PDF · ${REPORT_LAYOUT_LABELS[report?.layout] || report?.layout || ''}`);

export const savedReportsLink = ({ ownerCode, weekKey } = {}) => {
    const params = new URLSearchParams();
    if (ownerCode) params.set('owner', ownerCode);
    if (weekKey) params.set('week', weekKey);
    const query = params.toString();
    return `/remittance/reports${query ? `?${query}` : ''}`;
};

const authHeaders = (token) => ({ Authorization: `Bearer ${token}` });

// Server messages arrive as JSON, or as a Blob when the request asked for a file.
const readErrorMessage = async (error, fallback) => {
    const data = error?.response?.data;
    if (data instanceof Blob) {
        try {
            const parsed = JSON.parse(await data.text());
            return parsed.message || fallback;
        } catch (parseError) {
            return fallback;
        }
    }
    return data?.message || error?.message || fallback;
};

/** Generates a report on the server from the saved figures and stores it in Google Drive. */
export const generateSavedReport = async ({ token, ownerCode, weekKey, format, layout }) => {
    try {
        const response = await axios.post(
            `${rootUrl}/api/remittance-reports`,
            { owner_code: ownerCode, week_key: weekKey, format, layout },
            { headers: authHeaders(token) }
        );
        if (!response.data?.success) {
            throw new Error(response.data?.message || 'The report could not be saved.');
        }
        return response.data.data;
    } catch (error) {
        throw new Error(await readErrorMessage(error, 'The report could not be saved. Please try again.'));
    }
};

export const fetchSavedReportBlob = async ({ token, reportId }) => {
    try {
        const response = await axios.get(`${rootUrl}/api/remittance-reports/${reportId}/file`, {
            headers: authHeaders(token),
            responseType: 'blob'
        });
        return response.data;
    } catch (error) {
        throw new Error(await readErrorMessage(error, 'The saved file could not be opened.'));
    }
};

const triggerDownload = (blob, fileName) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 30000);
};

export const downloadSavedReport = async ({ token, report }) => {
    const blob = await fetchSavedReportBlob({ token, reportId: report.id });
    triggerDownload(blob, report.file_name);
};

/**
 * Opens a stored PDF in a new tab (CSV files are downloaded instead). The tab is
 * opened before the request so browsers don't treat it as an unrequested popup.
 */
export const viewSavedReport = async ({ token, report }) => {
    if (report.format !== 'pdf') {
        await downloadSavedReport({ token, report });
        return;
    }
    const tab = window.open('', '_blank');
    try {
        const blob = await fetchSavedReportBlob({ token, reportId: report.id });
        const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
        if (tab) {
            tab.location.href = url;
        } else {
            triggerDownload(blob, report.file_name);
        }
        setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
        if (tab) tab.close();
        throw error;
    }
};

export const emailSavedReport = async ({ token, reportId, recipientEmail, subject, message }) => {
    try {
        const response = await axios.post(
            `${rootUrl}/api/remittance-reports/${reportId}/email`,
            { recipient_email: recipientEmail || undefined, subject: subject || undefined, message: message || undefined },
            { headers: authHeaders(token) }
        );
        return response.data;
    } catch (error) {
        throw new Error(await readErrorMessage(error, 'The email could not be sent.'));
    }
};

export const createSavedReportShareLink = async ({ token, reportId }) => {
    try {
        const response = await axios.post(
            `${rootUrl}/api/remittance-reports/${reportId}/share-link`,
            {},
            { headers: authHeaders(token) }
        );
        const data = response.data?.data;
        return { ...data, url: `${rootUrl}/api/public/remittance-reports/${encodeURIComponent(data.token)}` };
    } catch (error) {
        throw new Error(await readErrorMessage(error, 'A share link could not be created.'));
    }
};

export const listSavedReports = async ({ token, filters = {} }) => {
    const params = {};
    Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') params[key] = value;
    });
    try {
        const response = await axios.get(`${rootUrl}/api/remittance-reports`, { headers: authHeaders(token), params });
        return response.data;
    } catch (error) {
        throw new Error(await readErrorMessage(error, 'Saved reports could not be loaded.'));
    }
};

export const fetchSavedReportEmails = async ({ token, reportId }) => {
    const response = await axios.get(`${rootUrl}/api/remittance-reports/${reportId}/emails`, { headers: authHeaders(token) });
    return response.data?.data || [];
};

// Default recipient, subject and message for the Email dialog, built on the server from the saved report.
export const fetchSavedReportEmailDraft = async ({ token, reportId }) => {
    const response = await axios.get(`${rootUrl}/api/remittance-reports/${reportId}/email-draft`, { headers: authHeaders(token) });
    return response.data?.data || null;
};
